import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isCrawlerClient } from "@/lib/crawler";

/**
 * Public read of the crawler indexing open-access flag
 * (`engine_settings.crawler_open_access`).
 *
 * Backed by the SECURITY DEFINER STABLE `is_indexing_open()` RPC granted to
 * both `anon` and `authenticated`.
 */
export function useIndexingFlag(): boolean {
  const query = useQuery({
    queryKey: ["indexing-open"],
    // Long cache — admin flips this rarely; refetch on window focus is fine.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("is_indexing_open");
      if (error) return false;
      return !!data;
    },
  });
  return query.data === true;
}

/**
 * The credit-gate bypass verdict.
 *
 * ON *and* the client is a crawler / prerender pass → picks and analysis open
 * so bots index real content. For every human session this returns `false`,
 * even while the flag is ON, so the credit economy always applies to visitors
 * and members. No robots/canonical behaviour depends on this value.
 */
export function useIndexingOpen(): boolean {
  const flag = useIndexingFlag();
  return flag && isCrawlerClient();
}
